import { assertSupabase, supabase } from '../lib/supabase';
import { Booking } from '../types';
import { parseDateOnlyLocal, formatLocalYYYYMMDD } from './dateUtils';
import { expandStayDates } from './stayDates';

export interface ConfirmedBooking extends Booking {
  campId: string;
  campName: string;
  confirmedAt: string; // ISO date string
  status: 'confirmed' | 'cancelled';
  paidAt?: string | null;
  stripeCheckoutSessionId?: string | null;
  stripePaymentIntentId?: string | null;
  stripeCustomerId?: string | null;
}

function rowToCheckInOut(row: any): { checkIn: Date; checkOut: Date } {
  const cin =
    typeof row.check_in === 'string'
      ? parseDateOnlyLocal(row.check_in)
      : new Date(row.check_in);
  const cout =
    typeof row.check_out === 'string'
      ? parseDateOnlyLocal(row.check_out)
      : new Date(row.check_out);
  return { checkIn: cin, checkOut: cout };
}

// Get all confirmed bookings from Supabase
export const getConfirmedBookings = async (): Promise<{
  bookings: ConfirmedBooking[];
  error?: string;
}> => {
  try {
    if (!supabase) {
      console.warn('Supabase not configured, returning empty array');
      return { bookings: [], error: 'Supabase is not configured' };
    }

    const { data, error } = await supabase
      .from('bookings')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error loading confirmed bookings:', error);
      return { bookings: [], error: error.message };
    }

    if (!data) return { bookings: [] };

    const bookings = data.map((row: any) => {
      const { checkIn, checkOut } = rowToCheckInOut(row);
      return {
        id: row.id,
        campId: row.camp_id,
        campName: row.camp_name,
        checkIn,
        checkOut,
        guests: row.guests,
        totalPrice: parseFloat(String(row.total_price)),
        guestName: row.guest_name,
        guestEmail: row.guest_email,
        guestPhone: row.guest_phone || '',
        specialRequests: row.special_requests,
        confirmedAt: row.confirmed_at,
        status: row.status as 'confirmed' | 'cancelled',
        paidAt: row.paid_at ?? null,
        stripeCheckoutSessionId: row.stripe_checkout_session_id ?? null,
        stripePaymentIntentId: row.stripe_payment_intent_id ?? null,
        stripeCustomerId: row.stripe_customer_id ?? null,
      };
    });
    return { bookings };
  } catch (error) {
    console.error('Error loading confirmed bookings:', error);
    return {
      bookings: [],
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/** Public lookup for guest payment/PDF flows (requires booking id). */
export const fetchConfirmedBookingById = async (
  bookingId: string
): Promise<{ booking: ConfirmedBooking | null; error?: string }> => {
  try {
    if (!supabase) {
      return { booking: null, error: 'Supabase is not configured' };
    }
    const trimmed = bookingId.trim();
    if (!trimmed) {
      return { booking: null, error: 'Missing booking ID' };
    }

    const { data, error } = await supabase
      .from('bookings')
      .select('*')
      .eq('id', trimmed)
      .eq('status', 'confirmed')
      .maybeSingle();

    if (error) {
      console.error('Error loading booking:', error);
      return { booking: null, error: error.message };
    }
    if (!data) return { booking: null };

    const { checkIn, checkOut } = rowToCheckInOut(data);
    const booking: ConfirmedBooking = {
      id: data.id,
      campId: data.camp_id,
      campName: data.camp_name,
      checkIn,
      checkOut,
      guests: data.guests,
      totalPrice: parseFloat(String(data.total_price)),
      guestName: data.guest_name,
      guestEmail: data.guest_email,
      guestPhone: data.guest_phone || '',
      specialRequests: data.special_requests,
      confirmedAt: data.confirmed_at,
      status: 'confirmed',
      paidAt: data.paid_at ?? null,
      stripeCheckoutSessionId: data.stripe_checkout_session_id ?? null,
      stripePaymentIntentId: data.stripe_payment_intent_id ?? null,
      stripeCustomerId: data.stripe_customer_id ?? null,
    };
    return { booking };
  } catch (error) {
    console.error('Error loading booking:', error);
    return {
      booking: null,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

// Save a confirmed booking to Supabase (uses RPC — anon cannot upsert under RLS)
export const saveConfirmedBooking = async (
  booking: ConfirmedBooking
): Promise<{ success: boolean; error?: string }> => {
  try {
    if (!supabase) {
      return { success: false, error: 'Supabase is not configured' };
    }

    const { error } = await supabase.rpc('save_approved_booking', {
      p_id: booking.id,
      p_camp_id: booking.campId,
      p_camp_name: booking.campName,
      p_check_in: formatLocalYYYYMMDD(booking.checkIn),
      p_check_out: formatLocalYYYYMMDD(booking.checkOut),
      p_guests: booking.guests,
      p_total_price: booking.totalPrice,
      p_guest_name: booking.guestName,
      p_guest_email: booking.guestEmail,
      p_guest_phone: booking.guestPhone ?? '',
      p_special_requests: booking.specialRequests ?? '',
      p_status: booking.status,
    });

    if (error) {
      console.error('Error saving confirmed booking:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Error saving confirmed booking:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

// Cancel a booking in Supabase
export const cancelBooking = async (bookingId: string): Promise<boolean> => {
  try {
    const client = assertSupabase();

    const { error } = await client
      .from('bookings')
      .update({ status: 'cancelled' })
      .eq('id', bookingId);

    if (error) {
      console.error('Error cancelling booking:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error cancelling booking:', error);
    return false;
  }
};

// Delete a booking permanently from Supabase
export const deleteBooking = async (bookingId: string): Promise<boolean> => {
  try {
    const client = assertSupabase();

    const { error } = await client
      .from('bookings')
      .delete()
      .eq('id', bookingId);

    if (error) {
      console.error('Error deleting booking:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error deleting booking:', error);
    return false;
  }
};

// Get all dates blocked by confirmed bookings for a specific camp
export const getBlockedDatesForCamp = async (campId: string): Promise<string[]> => {
  try {
    if (!supabase) return [];

    const { data, error } = await supabase
      .from('bookings')
      .select('check_in, check_out')
      .eq('camp_id', campId)
      .eq('status', 'confirmed');

    if (error || !data) {
      console.error('Error loading blocked dates:', error);
      return [];
    }

    const blockedDates: string[] = [];
    
    data.forEach((booking: any) => {
      const { checkIn, checkOut } = rowToCheckInOut(booking);
      blockedDates.push(...expandStayDates(checkIn, checkOut));
    });
    
    return Array.from(new Set(blockedDates));
  } catch (error) {
    console.error('Error getting blocked dates:', error);
    return [];
  }
};

export interface ManualBlockedDateRow {
  date: string;
  campId: string | null;
}

export interface BookingRequest {
  id: string;
  campId: string;
  campName: string;
  checkIn: Date;
  checkOut: Date;
  guests: number;
  totalPrice: number;
  guestName: string;
  guestEmail: string;
  guestPhone: string;
  specialRequests?: string;
  status: 'pending' | 'approved' | 'declined';
  createdAt: string;
}

export const getManualBlockedDateRows = async (): Promise<ManualBlockedDateRow[]> => {
  try {
    if (!supabase) return [];

    const { data, error } = await supabase.from('blocked_dates').select('date, camp_id');

    if (error || !data) {
      console.error('Error loading manual blocked date rows:', error);
      return [];
    }

    return data.map((row: { date: string; camp_id: string | null }) => ({
      date: row.date,
      campId: row.camp_id ?? null,
    }));
  } catch (error) {
    console.error('Error loading manual blocked date rows:', error);
    return [];
  }
};

export const getPendingBookingRequests = async (): Promise<{
  requests: BookingRequest[];
  error?: string;
}> => {
  try {
    if (!supabase) {
      return { requests: [], error: 'Supabase is not configured' };
    }

    const { data, error } = await supabase
      .from('booking_requests')
      .select('*')
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error loading pending booking requests:', error);
      return { requests: [], error: error.message };
    }

    if (!data) return { requests: [] };

    const requests = data.map((row: Record<string, unknown>) => {
      const { checkIn, checkOut } = rowToCheckInOut(row);
      return {
        id: String(row.id),
        campId: String(row.camp_id),
        campName: String(row.camp_name),
        checkIn,
        checkOut,
        guests: Number(row.guests),
        totalPrice: parseFloat(String(row.total_price)),
        guestName: String(row.guest_name),
        guestEmail: String(row.guest_email),
        guestPhone: String(row.guest_phone || ''),
        specialRequests: row.special_requests ? String(row.special_requests) : undefined,
        status: row.status as BookingRequest['status'],
        createdAt: String(row.created_at),
      };
    });

    return { requests };
  } catch (error) {
    console.error('Error loading pending booking requests:', error);
    return {
      requests: [],
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

export const saveBookingRequest = async (
  booking: Booking,
  campName: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    if (!supabase) {
      return { success: false, error: 'Supabase is not configured' };
    }

    const { error } = await supabase.rpc('submit_booking_request', {
      p_id: booking.id,
      p_camp_id: booking.campId,
      p_camp_name: campName,
      p_check_in: formatLocalYYYYMMDD(booking.checkIn),
      p_check_out: formatLocalYYYYMMDD(booking.checkOut),
      p_guests: booking.guests,
      p_total_price: booking.totalPrice,
      p_guest_name: booking.guestName,
      p_guest_email: booking.guestEmail,
      p_guest_phone: booking.guestPhone ?? '',
      p_special_requests: booking.specialRequests ?? '',
    });

    if (error) {
      console.error('Error saving booking request:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Error saving booking request:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

export const markBookingRequestStatus = async (
  bookingId: string,
  status: 'approved' | 'declined'
): Promise<boolean> => {
  try {
    if (!supabase) return false;

    const { error } = await supabase.rpc('mark_booking_request_status', {
      p_id: bookingId,
      p_status: status,
    });

    if (error) {
      console.error('Error updating booking request status:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error updating booking request status:', error);
    return false;
  }
};

// Get manually blocked dates from Supabase (camp-specific or 'all' for all camps)
export const getManualBlockedDates = async (campId?: string): Promise<string[]> => {
  try {
    if (!supabase) return [];

    let query = supabase
      .from('blocked_dates')
      .select('date, camp_id');

    if (campId) {
      // Get dates blocked for this camp OR for all camps (camp_id is NULL)
      query = query.or(`camp_id.eq.${campId},camp_id.is.null`);
    }

    const { data, error } = await query;

    if (error || !data) {
      console.error('Error loading manual blocked dates:', error);
      return [];
    }

    return data.map((row: any) => row.date);
  } catch (error) {
    console.error('Error loading manual blocked dates:', error);
    return [];
  }
};

// Add manually blocked dates to Supabase
export const addManualBlockedDates = async (dates: string[], campId: string = 'all'): Promise<boolean> => {
  try {
    const client = assertSupabase();

    const records = dates.map(date => ({
      camp_id: campId === 'all' ? null : campId,
      date: date,
    }));

    const { error } = await client
      .from('blocked_dates')
      .upsert(records, {
        onConflict: 'camp_id,date',
        ignoreDuplicates: false
      });

    if (error) {
      console.error('Error saving manual blocked dates:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error saving manual blocked dates:', error);
    return false;
  }
};

// Remove manually blocked dates from Supabase
export const removeManualBlockedDates = async (dates: string[], campId?: string): Promise<boolean> => {
  try {
    const client = assertSupabase();

    let query = client
      .from('blocked_dates')
      .delete()
      .in('date', dates);

    if (campId) {
      query = query.or(`camp_id.eq.${campId},camp_id.is.null`);
    }

    const { error } = await query;

    if (error) {
      console.error('Error removing manual blocked dates:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error removing manual blocked dates:', error);
    return false;
  }
};

// Get all blocked dates (manual + confirmed bookings) for a camp
export const getAllBlockedDatesForCamp = async (campId: string): Promise<string[]> => {
  try {
    const [bookingDates, manualDates] = await Promise.all([
      getBlockedDatesForCamp(campId),
      getManualBlockedDates(campId)
    ]);

    return Array.from(new Set([...bookingDates, ...manualDates]));
  } catch (error) {
    console.error('Error getting all blocked dates:', error);
    return [];
  }
};
